/**
 * Distribution Reducers
 * 
 * Redux reducers for distribution monitoring state management.
 * Handles state updates for distribution lists, stats, and reviews.
 * 
 * @module DistributionReducers
 * @category Reducers
 */

import {
    DISTRIBUTION_LIST_REQUEST,
    DISTRIBUTION_LIST_SUCCESS,
    DISTRIBUTION_LIST_FAIL,
    DISTRIBUTION_LIST_RESET,
    DISTRIBUTION_STATS_REQUEST,
    DISTRIBUTION_STATS_SUCCESS,
    DISTRIBUTION_STATS_FAIL,
    FLAGGED_DISTRIBUTIONS_REQUEST,
    FLAGGED_DISTRIBUTIONS_SUCCESS,
    FLAGGED_DISTRIBUTIONS_FAIL,
    DISTRIBUTION_REVIEW_REQUEST,
    DISTRIBUTION_REVIEW_SUCCESS,
    DISTRIBUTION_REVIEW_FAIL,
    DISTRIBUTION_REVIEW_RESET,
    DISTRIBUTION_BULK_REVIEW_REQUEST,
    DISTRIBUTION_BULK_REVIEW_SUCCESS,
    DISTRIBUTION_BULK_REVIEW_FAIL,
    DISTRIBUTION_BULK_REVIEW_RESET,
    AGENT_LEADERBOARD_REQUEST,
    AGENT_LEADERBOARD_SUCCESS,
    AGENT_LEADERBOARD_FAIL,
    DISTRIBUTION_REPORTS_REQUEST,
    DISTRIBUTION_REPORTS_SUCCESS,
    DISTRIBUTION_REPORTS_FAIL,
    DISTRIBUTION_REPORT_EXPORT_REQUEST,
    DISTRIBUTION_REPORT_EXPORT_SUCCESS,
    DISTRIBUTION_REPORT_EXPORT_FAIL,
    DISTRIBUTION_REPORT_EXPORT_RESET,
    DISTRIBUTION_REPORT_RECORDS_REQUEST,
    DISTRIBUTION_REPORT_RECORDS_SUCCESS,
    DISTRIBUTION_REPORT_RECORDS_FAIL,
    DISTRIBUTION_REPORT_RECORDS_EXPORT_REQUEST,
    DISTRIBUTION_REPORT_RECORDS_EXPORT_SUCCESS,
    DISTRIBUTION_REPORT_RECORDS_EXPORT_FAIL,
    DISTRIBUTION_REPORT_RECORDS_EXPORT_RESET,
    FINAL_REMINDER_CANDIDATES_REQUEST,
    FINAL_REMINDER_CANDIDATES_SUCCESS,
    FINAL_REMINDER_CANDIDATES_FAIL,
    FINAL_REMINDER_EXPORT_REQUEST,
    FINAL_REMINDER_EXPORT_SUCCESS,
    FINAL_REMINDER_EXPORT_FAIL,
    FINAL_REMINDER_EXPORT_RESET,
} from '../constants/distributionConstants';

const normalizePaginatedPayload = (payload = {}) => {
    const data = Array.isArray(payload.data) ? payload.data : [];
    const limit = Math.max(1, Number(payload.limit) || data.length || 1);
    const total = Math.max(0, Number(payload.total) || data.length);
    const page = Math.max(1, Number(payload.page) || 1);

    return {
        data,
        page,
        limit,
        total,
        pages: Math.max(1, Math.ceil(total / limit))
    };
};

/**
 * Distribution list reducer
 * Handles fetching and filtering distribution records
 */
export const distributionListReducer = (state = { distributions: [] }, action) => {
    switch (action.type) {
        case DISTRIBUTION_LIST_REQUEST:
            return { loading: true, distributions: [] };
        case DISTRIBUTION_LIST_SUCCESS: {
            const distributionListPayload = normalizePaginatedPayload(action.payload);
            return {
                loading: false,
                distributions: distributionListPayload.data,
                page: distributionListPayload.page,
                pages: distributionListPayload.pages,
                total: distributionListPayload.total,
                limit: distributionListPayload.limit
            };
        }
        case DISTRIBUTION_LIST_FAIL:
            return { loading: false, error: action.payload };
        case DISTRIBUTION_LIST_RESET:
            return { distributions: [] };
        default:
            return state;
    }
};

/**
 * Distribution stats reducer
 * Handles dashboard statistics
 */
export const distributionStatsReducer = (state = {}, action) => {
    switch (action.type) {
        case DISTRIBUTION_STATS_REQUEST:
            return { loading: true };
        case DISTRIBUTION_STATS_SUCCESS:
            return {
                loading: false,
                stats: action.payload
            };
        case DISTRIBUTION_STATS_FAIL:
            return { loading: false, error: action.payload };
        default:
            return state;
    }
};

/**
 * Flagged distributions reducer
 * Handles fetching distributions flagged for review
 */
export const flaggedDistributionsReducer = (state = { distributions: [] }, action) => {
    switch (action.type) {
        case FLAGGED_DISTRIBUTIONS_REQUEST:
            return { loading: true, distributions: [] };
        case FLAGGED_DISTRIBUTIONS_SUCCESS: {
            const flaggedPayload = normalizePaginatedPayload(action.payload);
            return {
                loading: false,
                distributions: flaggedPayload.data,
                page: flaggedPayload.page,
                pages: flaggedPayload.pages,
                total: flaggedPayload.total,
                limit: flaggedPayload.limit
            };
        }
        case FLAGGED_DISTRIBUTIONS_FAIL:
            return { loading: false, error: action.payload };
        default:
            return state;
    }
};

/**
 * Distribution review reducer
 * Handles reviewing individual flagged distributions
 */
export const distributionReviewReducer = (state = {}, action) => {
    switch (action.type) {
        case DISTRIBUTION_REVIEW_REQUEST:
            return { loading: true };
        case DISTRIBUTION_REVIEW_SUCCESS:
            return {
                loading: false,
                success: true,
                distribution: action.payload
            };
        case DISTRIBUTION_REVIEW_FAIL:
            return { loading: false, error: action.payload };
        case DISTRIBUTION_REVIEW_RESET:
            return {};
        default:
            return state;
    }
};

/**
 * Bulk distribution review reducer
 * Handles reviewing multiple distributions at once
 */
export const distributionBulkReviewReducer = (state = {}, action) => {
    switch (action.type) {
        case DISTRIBUTION_BULK_REVIEW_REQUEST:
            return { loading: true };
        case DISTRIBUTION_BULK_REVIEW_SUCCESS:
            return {
                loading: false,
                success: true,
                result: action.payload
            };
        case DISTRIBUTION_BULK_REVIEW_FAIL:
            return { loading: false, error: action.payload };
        case DISTRIBUTION_BULK_REVIEW_RESET:
            return {};
        default:
            return state;
    }
};

/**
 * Agent leaderboard reducer
 * Handles fetching agent performance rankings
 */
export const agentLeaderboardReducer = (state = { agents: [] }, action) => {
    switch (action.type) {
        case AGENT_LEADERBOARD_REQUEST:
            return { loading: true, agents: [] };
        case AGENT_LEADERBOARD_SUCCESS:
            return {
                loading: false,
                agents: action.payload
            };
        case AGENT_LEADERBOARD_FAIL:
            return { loading: false, error: action.payload };
        default:
            return state;
    }
};

/**
 * Distribution reports reducer
 * Handles advanced analytics datasets (trend, by-agent, by-LGA)
 */
export const distributionReportsReducer = (state = {}, action) => {
    switch (action.type) {
        case DISTRIBUTION_REPORTS_REQUEST:
            return { ...state, loading: true };
        case DISTRIBUTION_REPORTS_SUCCESS:
            return {
                loading: false,
                reports: action.payload
            };
        case DISTRIBUTION_REPORTS_FAIL:
            return { loading: false, error: action.payload };
        default:
            return state;
    }
};

/**
 * Distribution report export reducer
 * Tracks report file export state
 */
export const distributionReportExportReducer = (state = {}, action) => {
    switch (action.type) {
        case DISTRIBUTION_REPORT_EXPORT_REQUEST:
            return { loading: true };
        case DISTRIBUTION_REPORT_EXPORT_SUCCESS:
            return {
                loading: false,
                success: true,
                file: action.payload
            };
        case DISTRIBUTION_REPORT_EXPORT_FAIL:
            return { loading: false, error: action.payload };
        case DISTRIBUTION_REPORT_EXPORT_RESET:
            return {};
        default:
            return state;
    }
};

/**
 * Distribution report records reducer
 * Handles detailed records table data
 */
export const distributionReportRecordsReducer = (state = { data: [] }, action) => {
    switch (action.type) {
        case DISTRIBUTION_REPORT_RECORDS_REQUEST:
            return { ...state, loading: true };
        case DISTRIBUTION_REPORT_RECORDS_SUCCESS:
            return {
                loading: false,
                ...action.payload,
                data: action.payload?.data || []
            };
        case DISTRIBUTION_REPORT_RECORDS_FAIL:
            return { loading: false, error: action.payload, data: [] };
        default:
            return state;
    }
};

/**
 * Distribution report records export reducer
 */
export const distributionReportRecordsExportReducer = (state = {}, action) => {
    switch (action.type) {
        case DISTRIBUTION_REPORT_RECORDS_EXPORT_REQUEST:
            return { loading: true };
        case DISTRIBUTION_REPORT_RECORDS_EXPORT_SUCCESS:
            return {
                loading: false,
                success: true,
                file: action.payload
            };
        case DISTRIBUTION_REPORT_RECORDS_EXPORT_FAIL:
            return { loading: false, error: action.payload };
        case DISTRIBUTION_REPORT_RECORDS_EXPORT_RESET:
            return {};
        default:
            return state;
    }
};

export const finalReminderCandidatesReducer = (state = { data: [], meta: {} }, action) => {
    switch (action.type) {
        case FINAL_REMINDER_CANDIDATES_REQUEST:
            return { loading: true, data: [], meta: {} };
        case FINAL_REMINDER_CANDIDATES_SUCCESS:
            return {
                loading: false,
                data: action.payload?.data || [],
                meta: action.payload?.meta || {},
            };
        case FINAL_REMINDER_CANDIDATES_FAIL:
            return { loading: false, error: action.payload, data: [], meta: {} };
        default:
            return state;
    }
};

export const finalReminderExportReducer = (state = {}, action) => {
    switch (action.type) {
        case FINAL_REMINDER_EXPORT_REQUEST:
            return { loading: true };
        case FINAL_REMINDER_EXPORT_SUCCESS:
            return { loading: false, success: true, file: action.payload };
        case FINAL_REMINDER_EXPORT_FAIL:
            return { loading: false, error: action.payload };
        case FINAL_REMINDER_EXPORT_RESET:
            return {};
        default:
            return state;
    }
};
